import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Plus, Trash2, Edit, Eye, Loader2, Search, 
  FileText, Target, MoreVertical 
} from 'lucide-react';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Checkbox } from '../components/ui/checkbox';
import api from '../lib/api';
import { toast } from 'sonner';
import { cn, formatCurrency, formatNumber } from '../lib/utils';

const PLATFORM_COLORS = {
  'DV360': 'bg-blue-100 text-blue-700',
  'Meta': 'bg-indigo-100 text-indigo-700',
  'Mondiads': 'bg-green-100 text-green-700',
  'Clickadu': 'bg-purple-100 text-purple-700',
  'PropellerAds': 'bg-orange-100 text-orange-700',
  'LinkedIn': 'bg-sky-100 text-sky-700',
  'TikTok': 'bg-pink-100 text-pink-700',
  'Twitter': 'bg-slate-100 text-slate-700',
};

export default function CampaignGroups() {
  const navigate = useNavigate();
  const [groups, setGroups] = useState([]);
  const [availableCampaigns, setAvailableCampaigns] = useState([]);
  const [reportTemplates, setReportTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [editingGroup, setEditingGroup] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [platformFilter, setPlatformFilter] = useState('all');
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    linked_campaigns: [], // Now includes: platform, external_campaign_id, campaign_name, currency, budget
    report_template_id: '',
    notes: ''
  });
  
  // Campaign selection state
  const [campaignSearchQuery, setCampaignSearchQuery] = useState('');
  const [campaignPlatformFilter, setCampaignPlatformFilter] = useState('all');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [groupsRes, campaignsRes, templatesRes] = await Promise.all([
        api.get('/campaign-groups'),
        api.get('/campaign-groups/available-campaigns'),
        api.get('/report-templates')
      ]);
      setGroups(groupsRes.data || []);
      setAvailableCampaigns(campaignsRes.data || []);
      setReportTemplates(templatesRes.data || []);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateGroup = () => {
    setEditingGroup(null);
    setFormData({
      name: '',
      linked_campaigns: [],
      report_template_id: '',
      notes: ''
    });
    setCampaignSearchQuery('');
    setCampaignPlatformFilter('all');
    setIsDialogOpen(true);
  };

  const handleEditGroup = (group) => {
    setEditingGroup(group);
    setFormData({
      name: group.name,
      linked_campaigns: group.linked_campaigns.map(lc => ({
        platform: lc.platform,
        external_campaign_id: lc.external_campaign_id,
        campaign_name: lc.campaign_name,
        currency: lc.currency || 'USD',
        budget: lc.budget || 0
      })),
      report_template_id: group.report_template_id || '',
      notes: group.notes || ''
    });
    setCampaignSearchQuery('');
    setCampaignPlatformFilter('all');
    setIsDialogOpen(true);
  };

  const handleViewGroup = async (group) => {
    try {
      const response = await api.get(`/campaign-groups/${group.id}`);
      setSelectedGroup(response.data);
      setIsViewDialogOpen(true);
    } catch (error) {
      toast.error('Failed to fetch group details');
    }
  };

  const handleDeleteGroup = async (groupId) => {
    if (!window.confirm('Are you sure you want to delete this campaign group?')) return;
    
    try {
      await api.delete(`/campaign-groups/${groupId}`);
      toast.success('Campaign group deleted');
      fetchData();
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('You can only delete your own groups');
      } else {
        toast.error('Failed to delete group');
      }
    }
  };

  const handleSaveGroup = async () => {
    if (!formData.name.trim()) {
      toast.error('Please enter a group name');
      return;
    }
    
    if (formData.linked_campaigns.length === 0) {
      toast.error('Please select at least one campaign');
      return;
    }

    setSaving(true);
    try {
      // Calculate total budget from individual campaign budgets
      const totalBudget = formData.linked_campaigns.reduce((sum, lc) => sum + (parseFloat(lc.budget) || 0), 0);
      
      // Get primary currency (from first campaign or USD)
      const primaryCurrency = formData.linked_campaigns[0]?.currency || 'USD';
      
      const payload = {
        ...formData,
        report_template_id: formData.report_template_id || null,
        total_budget: totalBudget,
        currency: primaryCurrency
      };

      if (editingGroup) {
        await api.put(`/campaign-groups/${editingGroup.id}`, payload);
        toast.success('Campaign group updated');
      } else {
        await api.post('/campaign-groups', payload);
        toast.success('Campaign group created');
      }
      setIsDialogOpen(false);
      fetchData();
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('You can only edit your own groups');
      } else {
        toast.error('Failed to save campaign group');
      }
    } finally {
      setSaving(false);
    }
  };

  const toggleCampaignSelection = (campaign) => {
    const isSelected = formData.linked_campaigns.some(
      lc => lc.external_campaign_id === campaign.external_campaign_id && lc.platform === campaign.platform
    );

    if (isSelected) {
      setFormData({
        ...formData,
        linked_campaigns: formData.linked_campaigns.filter(
          lc => !(lc.external_campaign_id === campaign.external_campaign_id && lc.platform === campaign.platform)
        )
      });
    } else {
      setFormData({
        ...formData,
        linked_campaigns: [
          ...formData.linked_campaigns,
          {
            platform: campaign.platform,
            external_campaign_id: campaign.external_campaign_id,
            campaign_name: campaign.campaign_name,
            currency: campaign.currency || 'USD',
            budget: 0
          }
        ]
      });
    }
  };

  const updateCampaignBudget = (campaignId, platform, budget) => {
    setFormData({
      ...formData,
      linked_campaigns: formData.linked_campaigns.map(lc => 
        lc.external_campaign_id === campaignId && lc.platform === platform
          ? { ...lc, budget: parseFloat(budget) || 0 }
          : lc
      )
    });
  };

  // Calculate total budget grouped by currency
  const budgetsByCurrency = formData.linked_campaigns.reduce((acc, lc) => {
    const currency = lc.currency || 'USD';
    const budget = parseFloat(lc.budget) || 0;
    if (!acc[currency]) {
      acc[currency] = 0;
    }
    acc[currency] += budget;
    return acc;
  }, {});

  // Get unique platforms from available campaigns
  const platforms = [...new Set(availableCampaigns.map(c => c.platform))].sort();

  // Filter available campaigns for selection
  const filteredAvailableCampaigns = availableCampaigns.filter(c => {
    const name = c.campaign_name || '';
    const id = c.external_campaign_id || '';
    const matchesSearch = name.toLowerCase().includes(campaignSearchQuery.toLowerCase()) ||
                          id.toLowerCase().includes(campaignSearchQuery.toLowerCase());
    const matchesPlatform = campaignPlatformFilter === 'all' || c.platform === campaignPlatformFilter;
    return matchesSearch && matchesPlatform;
  });

  // Filter groups for display
  const filteredGroups = groups.filter(g => {
    const matchesSearch = g.name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesPlatform = platformFilter === 'all' || 
                            g.linked_campaigns?.some(lc => lc.platform === platformFilter);
    return matchesSearch && matchesPlatform;
  });

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Campaign Groups</h1>
            <p className="text-sm text-slate-500 mt-1">
              Group campaigns together with budget tracking and reporting templates
            </p>
          </div>
          <Button onClick={handleCreateGroup} data-testid="create-group-btn">
            <Plus className="w-4 h-4 mr-2" />
            Create Group
          </Button>
        </div>

        {/* Filters */}
        <div className="flex gap-4">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Search groups..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
              data-testid="group-search-input"
            />
          </div>
          <Select value={platformFilter} onValueChange={setPlatformFilter}>
            <SelectTrigger className="w-[180px]" data-testid="group-platform-filter">
              <SelectValue placeholder="All Platforms" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Platforms</SelectItem>
              {platforms.map(p => (
                <SelectItem key={p} value={p}>{p}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Groups Table */}
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Group Name</TableHead>
                  <TableHead>Campaigns</TableHead>
                  <TableHead>Report Template</TableHead>
                  <TableHead className="text-right">Total Budget</TableHead>
                  <TableHead className="text-right">Budget Spent</TableHead>
                  <TableHead className="text-right">Remaining</TableHead>
                  <TableHead>Created By</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredGroups.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-8 text-slate-500">
                      {groups.length === 0 
                        ? "No campaign groups yet. Create one to start grouping campaigns."
                        : "No groups match your filters."}
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredGroups.map((group) => {
                    const remainingBudget = (group.total_budget || 0) - (group.total_spend || 0);
                    const budgetUsedPercent = group.total_budget > 0 
                      ? Math.min((group.total_spend / group.total_budget) * 100, 100) 
                      : 0;
                    
                    return (
                      <TableRow 
                        key={group.id} 
                        data-testid={`group-row-${group.id}`}
                        className="cursor-pointer hover:bg-slate-50"
                        onClick={() => handleViewGroup(group)}
                      >
                        <TableCell className="font-medium">
                          <div className="flex items-center gap-2">
                            <Target className="w-4 h-4 text-slate-400" />
                            {group.name}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-1">
                            {group.linked_campaigns?.slice(0, 3).map((lc, idx) => (
                              <Badge 
                                key={idx} 
                                variant="secondary"
                                className={`text-xs ${PLATFORM_COLORS[lc.platform] || 'bg-slate-100'}`}
                              >
                                {lc.platform}
                              </Badge>
                            ))}
                            {(group.linked_campaigns?.length || 0) > 3 && (
                              <Badge variant="outline" className="text-xs">
                                +{group.linked_campaigns.length - 3} more
                              </Badge>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          {group.report_template_name ? (
                            <div className="flex items-center gap-1 text-sm">
                              <FileText className="w-3 h-3 text-slate-400" />
                              <span className="truncate max-w-[120px]">{group.report_template_name}</span>
                            </div>
                          ) : (
                            <span className="text-slate-400 text-sm">None</span>
                          )}
                        </TableCell>
                        <TableCell className="text-right font-mono text-sm">
                          {(() => {
                            // Calculate budgets grouped by currency from linked_campaigns
                            const budgetsByCurr = (group.linked_campaigns || []).reduce((acc, lc) => {
                              const curr = lc.currency || 'USD';
                              const budget = parseFloat(lc.budget) || 0;
                              if (!acc[curr]) acc[curr] = 0;
                              acc[curr] += budget;
                              return acc;
                            }, {});
                            const currencies = Object.keys(budgetsByCurr);
                            
                            if (currencies.length === 0) {
                              return formatCurrency(group.total_budget || 0, group.currency || 'USD');
                            } else if (currencies.length === 1) {
                              return formatCurrency(budgetsByCurr[currencies[0]], currencies[0]);
                            } else {
                              // Multiple currencies - show each on separate line
                              return (
                                <div className="flex flex-col items-end gap-0.5">
                                  {currencies.map(curr => (
                                    <span key={curr}>{formatCurrency(budgetsByCurr[curr], curr)}</span>
                                  ))}
                                </div>
                              );
                            }
                          })()}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex flex-col items-end">
                            <span className="font-mono text-sm">
                              {formatCurrency(group.total_spend || 0, group.spend_currency || group.currency || 'USD')}
                            </span>
                            {group.total_budget > 0 && (
                              <div className="w-16 h-1.5 bg-slate-200 rounded-full mt-1">
                                <div 
                                  className={cn(
                                    "h-full rounded-full",
                                    budgetUsedPercent >= 90 ? "bg-red-500" : 
                                    budgetUsedPercent >= 70 ? "bg-yellow-500" : "bg-green-500"
                                  )}
                                  style={{ width: `${budgetUsedPercent}%` }}
                                />
                              </div>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className={cn(
                          "text-right font-mono text-sm",
                          remainingBudget < 0 ? "text-red-600" : "text-green-600"
                        )}>
                          {formatCurrency(remainingBudget, group.spend_currency || group.currency || 'USD')}
                        </TableCell>
                        <TableCell>
                          <span className="text-sm text-slate-500">{group.created_by_name || '-'}</span>
                        </TableCell>
                        <TableCell className="text-right">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                              <Button variant="ghost" size="sm">
                                <MoreVertical className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleViewGroup(group); }}>
                                <Eye className="w-4 h-4 mr-2" />
                                View Details
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleEditGroup(group); }}>
                                <Edit className="w-4 h-4 mr-2" />
                                Edit
                              </DropdownMenuItem>
                              <DropdownMenuItem 
                                onClick={(e) => { e.stopPropagation(); handleDeleteGroup(group.id); }}
                                className="text-red-600"
                              >
                                <Trash2 className="w-4 h-4 mr-2" />
                                Delete
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="w-[95vw] max-w-4xl max-h-[85vh] flex flex-col overflow-hidden">
          <DialogHeader className="shrink-0">
            <DialogTitle>
              {editingGroup ? 'Edit Campaign Group' : 'Create Campaign Group'}
            </DialogTitle>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto overflow-x-hidden space-y-5 py-4 px-1">
            {/* Group Name */}
            <div className="space-y-2">
              <Label htmlFor="group-name">Group Name *</Label>
              <Input
                id="group-name"
                placeholder="Enter group name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                data-testid="group-name-input"
                className="w-full"
              />
            </div>

            {/* Campaign Selection */}
            <div className="space-y-2">
              <Label>Select Campaigns * ({formData.linked_campaigns.length} selected)</Label>
              <div className="border rounded-lg p-3 space-y-3">
                {/* Search and Filter */}
                <div className="flex flex-col sm:flex-row gap-2">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      placeholder="Search campaigns..."
                      value={campaignSearchQuery}
                      onChange={(e) => setCampaignSearchQuery(e.target.value)}
                      className="pl-10 w-full"
                    />
                  </div>
                  <Select value={campaignPlatformFilter} onValueChange={setCampaignPlatformFilter}>
                    <SelectTrigger className="w-full sm:w-[140px] shrink-0">
                      <SelectValue placeholder="Platform" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Platforms</SelectItem>
                      {platforms.map(p => (
                        <SelectItem key={p} value={p}>{p}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Campaign List */}
                <div className="max-h-[150px] overflow-y-auto space-y-1 border rounded p-2">
                  {filteredAvailableCampaigns.length === 0 ? (
                    <p className="text-sm text-slate-500 text-center py-4">
                      No campaigns found. Sync platform data first.
                    </p>
                  ) : (
                    filteredAvailableCampaigns.map((campaign, idx) => {
                      const isSelected = formData.linked_campaigns.some(
                        lc => lc.external_campaign_id === campaign.external_campaign_id && 
                              lc.platform === campaign.platform
                      );
                      return (
                        <label
                          key={`${campaign.platform}-${campaign.external_campaign_id}-${idx}`}
                          className={cn(
                            "flex items-center gap-2 p-2 rounded cursor-pointer hover:bg-slate-50",
                            isSelected && "bg-blue-50"
                          )}
                        >
                          <Checkbox
                            checked={isSelected}
                            onCheckedChange={() => toggleCampaignSelection(campaign)}
                            className="shrink-0"
                          />
                          <div className="flex-1 min-w-0 overflow-hidden">
                            <p className="text-sm font-medium truncate">{campaign.campaign_name}</p>
                            <p className="text-xs text-slate-500 truncate">
                              ID: {campaign.external_campaign_id} • Spend: {formatCurrency(campaign.total_spend || 0, campaign.currency || 'USD')}
                            </p>
                          </div>
                          <Badge className={`text-xs shrink-0 ${PLATFORM_COLORS[campaign.platform] || 'bg-slate-100'}`}>
                            {campaign.platform}
                          </Badge>
                        </label>
                      );
                    })
                  )}
                </div>
              </div>
            </div>

            {/* Report Template */}
            <div className="space-y-2">
              <Label>Reporting Template</Label>
              <Select 
                value={formData.report_template_id || 'none'} 
                onValueChange={(val) => setFormData({ ...formData, report_template_id: val === 'none' ? '' : val })}
              >
                <SelectTrigger data-testid="report-template-select" className="w-full">
                  <SelectValue placeholder="Select a template (optional)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No template</SelectItem>
                  {reportTemplates.map(t => (
                    <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500">
                Templates are created in Performance Reports &gt; Save Template
              </p>
            </div>

            {/* Selected Campaigns with Individual Budgets */}
            {formData.linked_campaigns.length > 0 && (
              <div className="space-y-2">
                <Label>Campaign Budgets</Label>
                <div className="border rounded-lg overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-slate-50">
                        <TableHead className="text-xs min-w-[250px]">Campaign</TableHead>
                        <TableHead className="text-xs text-center w-[80px]">Currency</TableHead>
                        <TableHead className="text-xs w-[160px]">Budget</TableHead>
                        <TableHead className="text-xs w-[50px]"></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {formData.linked_campaigns.map((lc, idx) => (
                        <TableRow key={`${lc.platform}-${lc.external_campaign_id}-${idx}`}>
                          <TableCell className="py-2">
                            <div className="flex items-center gap-2">
                              <Badge className={`text-xs shrink-0 ${PLATFORM_COLORS[lc.platform] || 'bg-slate-100'}`}>
                                {lc.platform}
                              </Badge>
                              <span className="text-sm truncate max-w-[300px]" title={lc.campaign_name}>{lc.campaign_name}</span>
                            </div>
                          </TableCell>
                          <TableCell className="py-2 text-center">
                            <span className="text-sm font-medium text-slate-700">{lc.currency || 'USD'}</span>
                          </TableCell>
                          <TableCell className="py-2">
                            <Input
                              type="number"
                              min="0"
                              step="0.01"
                              placeholder="Enter budget"
                              value={lc.budget || ''}
                              onChange={(e) => updateCampaignBudget(lc.external_campaign_id, lc.platform, e.target.value)}
                              className="h-9 text-sm w-full"
                              data-testid={`budget-${lc.external_campaign_id}`}
                            />
                          </TableCell>
                          <TableCell className="py-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => toggleCampaignSelection(lc)}
                              className="h-8 w-8 p-0 text-red-500 hover:text-red-700"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  {/* Total Budget Summary - Grouped by Currency */}
                  <div className="bg-slate-50 px-4 py-3 border-t">
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium text-slate-600">Total Budget:</span>
                      <div className="flex flex-wrap gap-3 justify-end">
                        {Object.entries(budgetsByCurrency).length === 0 ? (
                          <span className="text-lg font-bold text-slate-400">-</span>
                        ) : Object.entries(budgetsByCurrency).map(([currency, total]) => (
                          <span key={currency} className="text-lg font-bold">
                            {formatCurrency(total, currency)}
                          </span>
                        ))}
                      </div>
                    </div>
                    {Object.keys(budgetsByCurrency).length > 1 && (
                      <p className="text-xs text-amber-600 mt-1 text-right">
                        ⚠️ Mixed currencies - totals shown separately
                      </p>
                    )}
                  </div>
                </div>
                <p className="text-xs text-slate-500">
                  Set individual budget for each campaign. Currency is based on ad account settings.
                </p>
              </div>
            )}

            {/* Notes */}
            <div className="space-y-2">
              <Label htmlFor="notes">Notes (optional)</Label>
              <Textarea
                id="notes"
                placeholder="Add any notes about this group..."
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                rows={2}
                className="w-full resize-none"
              />
            </div>
          </div>

          <DialogFooter className="shrink-0 border-t pt-4 gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveGroup} disabled={saving} data-testid="save-group-btn">
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {editingGroup ? 'Update Group' : 'Create Group'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Details Dialog */}
      <Dialog open={isViewDialogOpen} onOpenChange={setIsViewDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Target className="w-5 h-5" />
              {selectedGroup?.name}
            </DialogTitle>
          </DialogHeader>

          {selectedGroup && (
            <div className="space-y-6 py-4">
              {/* Summary Cards */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-slate-50 rounded-lg p-4">
                  <p className="text-xs text-slate-500 uppercase">Total Budget</p>
                  <p className="text-xl font-bold">
                    {formatCurrency(selectedGroup.total_budget || 0, selectedGroup.currency || 'USD')}
                  </p>
                </div>
                <div className="bg-slate-50 rounded-lg p-4">
                  <p className="text-xs text-slate-500 uppercase">Budget Spent</p>
                  <p className="text-xl font-bold text-orange-600">
                    {formatCurrency(selectedGroup.total_spend || 0, selectedGroup.spend_currency || selectedGroup.currency || 'USD')}
                  </p>
                </div>
                <div className="bg-slate-50 rounded-lg p-4">
                  <p className="text-xs text-slate-500 uppercase">Remaining Budget</p>
                  <p className={cn(
                    "text-xl font-bold",
                    selectedGroup.remaining_budget < 0 ? "text-red-600" : "text-green-600"
                  )}>
                    {formatCurrency(selectedGroup.remaining_budget || 0, selectedGroup.spend_currency || selectedGroup.currency || 'USD')}
                  </p>
                </div>
                <div className="bg-slate-50 rounded-lg p-4">
                  <p className="text-xs text-slate-500 uppercase">Campaigns</p>
                  <p className="text-xl font-bold">
                    {selectedGroup.linked_campaigns?.length || 0}
                  </p>
                </div>
              </div>

              {/* Performance Summary */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-blue-50 rounded-lg p-4">
                  <p className="text-xs text-blue-600 uppercase">Impressions</p>
                  <p className="text-lg font-semibold">{formatNumber(selectedGroup.total_impressions || 0)}</p>
                </div>
                <div className="bg-green-50 rounded-lg p-4">
                  <p className="text-xs text-green-600 uppercase">Clicks</p>
                  <p className="text-lg font-semibold">{formatNumber(selectedGroup.total_clicks || 0)}</p>
                </div>
                <div className="bg-purple-50 rounded-lg p-4">
                  <p className="text-xs text-purple-600 uppercase">CTR</p>
                  <p className="text-lg font-semibold">
                    {selectedGroup.total_impressions > 0 
                      ? ((selectedGroup.total_clicks / selectedGroup.total_impressions) * 100).toFixed(2) 
                      : 0}%
                  </p>
                </div>
                <div className="bg-orange-50 rounded-lg p-4">
                  <p className="text-xs text-orange-600 uppercase">Conversions</p>
                  <p className="text-lg font-semibold">{formatNumber(selectedGroup.total_conversions || 0)}</p>
                </div>
              </div>

              {/* Report Template */}
              {selectedGroup.report_template_name && (
                <div className="flex items-center gap-2 p-3 bg-slate-50 rounded-lg">
                  <FileText className="w-4 h-4 text-slate-500" />
                  <span className="text-sm">Report Template: <strong>{selectedGroup.report_template_name}</strong></span>
                </div>
              )}

              {/* Linked Campaigns Table */}
              <div>
                <h3 className="font-medium mb-3">Linked Campaigns</h3>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Platform</TableHead>
                      <TableHead>Campaign</TableHead>
                      <TableHead className="text-right">Impressions</TableHead>
                      <TableHead className="text-right">Clicks</TableHead>
                      <TableHead className="text-right">Spend</TableHead>
                      <TableHead className="text-right">Conversions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {selectedGroup.linked_campaigns?.map((lc, idx) => (
                      <TableRow key={idx}>
                        <TableCell>
                          <Badge className={`text-xs ${PLATFORM_COLORS[lc.platform] || 'bg-slate-100'}`}>
                            {lc.platform}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div>
                            <p className="font-medium">{lc.campaign_name}</p>
                            <p className="text-xs text-slate-500">ID: {lc.external_campaign_id}</p>
                          </div>
                        </TableCell>
                        <TableCell className="text-right font-mono">{formatNumber(lc.impressions || 0)}</TableCell>
                        <TableCell className="text-right font-mono">{formatNumber(lc.clicks || 0)}</TableCell>
                        <TableCell className="text-right font-mono">{formatCurrency(lc.spend || 0, lc.currency || selectedGroup.spend_currency || 'USD')}</TableCell>
                        <TableCell className="text-right font-mono">{formatNumber(lc.conversions || 0)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {/* Notes */}
              {selectedGroup.notes && (
                <div>
                  <h3 className="font-medium mb-2">Notes</h3>
                  <p className="text-sm text-slate-600 bg-slate-50 p-3 rounded">{selectedGroup.notes}</p>
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsViewDialogOpen(false)}>
              Close
            </Button>
            <Button onClick={() => { setIsViewDialogOpen(false); handleEditGroup(selectedGroup); }}>
              <Edit className="w-4 h-4 mr-2" />
              Edit Group
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
